import { LegItem } from 'src/domains/sportsbook/betting/betSlipState/LegItem';
import { action, observable, computed, makeObservable } from 'mobx';
import { CombinationItem } from 'src/domains/sportsbook/betting/betSlipState/CombinationItem';
import { AddToAllState } from 'src/domains/sportsbook/betting/betSlipState/AddToAllState';
import { ReferralState } from 'src/domains/sportsbook/betting/betSlipState/ReferralState';
import { LegsState } from 'src/domains/sportsbook/betting/state/betSlipState/LegsState';
import { CombinationState } from 'src/domains/sportsbook/betting/state/betSlipState/CombinationsState';
import { CastBetsState } from 'src/domains/sportsbook/betting/state/betSlipState/CastBetsState';
import { PossibleBetsRequestState } from 'src/domains/sportsbook/betting/betSlipState/possibleBetsState/PossibleBetsState';
import { SuccessPlaceBetResponseType } from 'src/domains/sportsbook/betting/betting/postPlaceBetTypes';
import { BasicBetSlipState } from 'src/domains/sportsbook/betting/betSlipState/BasicBetSlipState';
import { BetSlipSummaryState } from 'src/domains/sportsbook/betting/state/betSlipState/BetSlipSummaryState';
import { BetSlipUserDataType } from 'src/domains/sportsbook/betting/betSlipState/BetSlipSheredTypes';
import { RabState } from 'src/domains/sportsbook/betting/state/rabState/RabState';
import { MultibetState } from 'src/domains/sportsbook/betting/state/multibetState/MultibetState';
import { BetsFilter, BetsState } from 'src/domains/sportsbook/betting/state/BetsState';
import { QuickBetState } from 'src/domains/sportsbook/betting/betSlipState/quickBetState/QuickBetState';
import { UnavailableSelectionsState } from 'src/domains/sportsbook/betting/betSlipState/UnavailableSelectionsState';
import { SdkCustomer } from 'src/domains/layouts/state/customer';
import { HtmlElementReactive } from 'src_common/common/mobx-utils/HtmlElementReactive';
import { UsersState } from 'src/domains/players/state/UsersState';
import { Amount } from 'src_common/common/amount/Amount';
import { PlaceBetBetsType } from 'src/domains/sportsbook/betting/betting/postPlaceBet';
import { BetslipIdsFactory } from 'src/domains/sportsbook/betting/models/BetslipIdsFactory';
import { SelectionId, WebsocketId } from 'src_common/common/websocket2/id/WebsocketId';
import { BetslipModelsContext } from 'src/domains/sportsbook/betting/models/BetslipModelsContext';
import { BetslipCombinationId, BetslipSingleId } from 'src/domains/sportsbook/betting/models/BetslipIdModels';
import { BetslipData } from 'src/domains/sportsbook/betting/betSlipState/BetslipData';
import { AutoMap, AutoMapSerialized } from 'src_common/common/mobx-utils/AutoMap';
import { Common } from 'src/domains/common/Common';
import { ConfigComponents } from 'src/domains/layouts/config/features/config';
import { Resource } from 'src_common/common/mobx-utils/Resource';
import { GetAvailableBonusesForAccountSuccessResponseType } from 'src_server/trpc/types/bonuses';
import { DateTime } from 'src_common/utils/time/time';
import { StarRouter } from 'src/domains/layouts/state/router/StarRouter';

export type SelectedBonusType = {
    selectionId: string | null;
    id: string;
    currentAmount: Amount;
    rawAmount: number;
};

export enum BetSlipFilter {
    BETSLIP = 'BETSLIP',
    MY_BETS = 'MY_BETS',
}

export enum BetSlipSectionFilter {
    SINGLES = 'SINGLES',
    MULTIPLES = 'MULTIPLES',
}

export class BetSlipState {
    public readonly ids: BetslipIdsFactory;
    public readonly addToAllState: AddToAllState;
    public readonly rabState: RabState;
    public readonly possibleBetsRequestState: PossibleBetsRequestState;
    public readonly castBetsState: CastBetsState;
    public readonly basicBetSlipState: BasicBetSlipState;
    public readonly betSlipSummaryState: BetSlipSummaryState;
    public readonly referralState: ReferralState;
    public readonly legsState: LegsState;
    public refElem: HTMLElement | null = null;
    private readonly combinationState: CombinationState;
    public readonly quickBetState: QuickBetState;
    public readonly unavailableSelectionsState: UnavailableSelectionsState;
    public readonly sdkCustomer: SdkCustomer;

    private readonly accountBonusesResource: Resource<GetAvailableBonusesForAccountSuccessResponseType | null>;
    @observable public isBonusPopupOpen: boolean = false;
    @observable public selectedBonuses: Map<string, Array<SelectedBonusType>> = new Map();
    @observable public selectionFreeBets: { id: string; freeBets: Array<string>; isCombination: boolean } | null = null;

    @observable public readonly reactiveWrapper: HtmlElementReactive<HTMLElement> = new HtmlElementReactive(500);
    @observable public selectedTab: string = BetSlipFilter.BETSLIP;
    public readonly betslipData: BetslipData;

    public forViewLegsItemMap: AutoMapSerialized<BetslipSingleId, LegItem>;
    public forViewCombinationsItemMap: AutoMap<string, CombinationItem>;

    public constructor(
        websocketId: WebsocketId,
        rabState: RabState,
        private readonly betsState: BetsState,
        private readonly common: Common
    ) {
        makeObservable(this);
        this.quickBetState = QuickBetState.getInstance();
        this.rabState = rabState;
        this.sdkCustomer = SdkCustomer.get(this.common);

        this.addToAllState = new AddToAllState(this.getLegsIds, ConfigComponents.get(common).config.decimalLength);

        const context = new BetslipModelsContext(this.getLegItemById, this.getCombinationItemById, this.addToAllState);
        this.ids = new BetslipIdsFactory(websocketId, context);

        this.unavailableSelectionsState = new UnavailableSelectionsState(
            this.quickBetState,
            () => this.legsIds2.length
        );

        this.betslipData = new BetslipData(
            this.ids,
            this.quickBetState,
            () => this.referralState,
            ConfigComponents.get(common).config.decimalLength,
            common,
            this.unavailableSelectionsState
        );

        this.possibleBetsRequestState = new PossibleBetsRequestState(
            this.sdkCustomer,
            this.getAccountData,
            this.rabState,
            MultibetState.get(common),
            () => this.basicBetSlipState.channel,
            () => this.betSlipSummaryState.isPlacingBet,
            this.betslipData,
            common,
            this.selectedBonuses
        );
        this.basicBetSlipState = new BasicBetSlipState(
            this.common,
            this.possibleBetsRequestState,
            this.quickBetState,
            this.getAccountData
        );
        this.referralState = new ReferralState(
            websocketId,
            this.ids,
            this.possibleBetsRequestState,
            this.basicBetSlipState,
            this.onPlaceBetSuccess,
            this.getAccountData,
            this.cleanAll,
            () => this.basicBetSlipState.channel,
            this.betslipData,
            common
        );

        this.combinationState = new CombinationState(
            this.sdkCustomer,
            this.possibleBetsRequestState,
            () => this.basicBetSlipState.channel,
            this.referralState,
            this.basicBetSlipState,
            this.ids,
            common
        );

        this.legsState = new LegsState(this.possibleBetsRequestState, this.sdkCustomer, this.betslipData);

        this.castBetsState = new CastBetsState(common, this.possibleBetsRequestState);

        this.betSlipSummaryState = new BetSlipSummaryState(
            this.possibleBetsRequestState,
            this.legsState,
            this.combinationState,
            this.rabState,
            MultibetState.get(common),
            this.referralState,
            this.basicBetSlipState,
            this.castBetsState,
            this.getFreeBetsAmount,
            this.getLegsIds,
            this.cleanAll,
            this.sdkCustomer,
            () => this.basicBetSlipState.channel,
            this.common,
            this.selectedBonuses,
            this.unavailableSelectionsState
        );

        this.forViewLegsItemMap = new AutoMapSerialized(
            (selectionId: BetslipSingleId) => selectionId.reactKey,
            (selectionId: BetslipSingleId) => {
                return new LegItem(
                    selectionId,
                    this.legsState,
                    this.referralState,
                    this.sdkCustomer,
                    this.betslipData,
                    this.common
                );
            }
        );

        this.forViewCombinationsItemMap = new AutoMap((combinationId: string) => {
            return new CombinationItem(combinationId, this.referralState, this.combinationState, this.betslipData);
        });

        this.accountBonusesResource = new Resource(
            async (): Promise<GetAvailableBonusesForAccountSuccessResponseType | null> => {
                const response = await this.common.trpcClient.client.bonuses.getAvailableBonusesForAccount.query();

                if (response.responseStatus === 'success') {
                    return response.data;
                }

                console.error(`Unexpected http response`, response);
                return null;
            }
        );
    }

    public getAccountData = (): BetSlipUserDataType => {
        const basicData = this.sdkCustomer.basicData.valueReady;
        const walletData = UsersState.get(this.common).walletData.valueReady;

        if (basicData !== null && walletData !== null) {
            return {
                currency: basicData.currency,
                country: basicData.country ?? '',
                balance: new Amount(walletData.playableBalance),
                accountAuthenticated: true,
                userId: basicData.id,
                ipUser: this.sdkCustomer.session.ipUser,
            };
        }

        return {
            currency: 'EUR',
            country: 'UK',
            accountAuthenticated: false,
            userId: null,
            balance: null,
            ipUser: null,
        };
    };

    @action public changeBetslipTab = (tabName: string): void => {
        this.selectedTab = tabName;
    };

    @action public redirectToCashOut = (): void => {
        if (this.sdkCustomer.session.userId === null) {
            StarRouter.get(this.common).redirectToLogin();
        } else {
            StarRouter.get(this.common).redirectToBetslip();
            this.betsState.changeSelectedFilter(BetsFilter.CASH_OUT);
            this.changeBetslipTab(BetSlipFilter.MY_BETS);
        }
    };

    @action public onRemoveAllLegs = (): void => {
        this.cleanAll();
    };

    @action private cleanAll = (): void => {
        console.log('cleanAll');
        this.betslipData.deleteMapCorePrepareLegs();
        this.betslipData.deleteMapCorePrepareCombinations();
        this.selectedBonuses.clear();
        this.unavailableSelectionsState.reset();

        this.possibleBetsRequestState.prevCorePossibleBetsResponse = null;

        // const legsItemKeys = this.legsState.forViewLegsItemMap.getAllKeys();
        // const combinationsItemKeys = this.combinationState.forViewCombinationsItemMap.getAllKeys();
        //this.addToAllState.stakeInputState.handleChange('');
        this.addToAllState.stakeInput.changeStake(new Amount('0'));

        this.referralState.cleanReferral();
        if (this.rabState.isBetBuilderOpen === true) {
            this.rabState.closeRab();
        }

        this.betSlipSummaryState.clearAllMultibetData();
        // for (const id of legsItemKeys) {
        //     this.legsState.forViewLegsItemMap.delete(id);

        // for (const id of combinationsItemKeys) {
        //     this.combinationState.forViewCombinationsItemMap.delete(id);
        // }
    };

    private getFreeBetsAmount = (): Amount | null => {
        return this.freeBetsAmount;
    };

    private getLegsIds = (): Array<BetslipSingleId> => {
        return this.legsIds2;
    };

    private getLegItemById = (legId: BetslipSingleId): LegItem => {
        return this.forViewLegsItemMap.get(legId);
    };

    public getLegItemByIdSelection = (selectionId: SelectionId, index: number): LegItem => {
        return this.ids.getSingleId(selectionId, index).getModel();
    };

    public getCombinationItemById = (combinationType: string): CombinationItem => {
        return this.forViewCombinationsItemMap.get(combinationType);
    };

    private onPlaceBetSuccess = (
        fromFreeBets: boolean,
        bets: PlaceBetBetsType | Array<SuccessPlaceBetResponseType>
    ): void => {
        this.betSlipSummaryState.onPlaceBetSuccess(fromFreeBets, bets);
    };

    @computed public get freeBetsAmount(): Amount | null {
        const usersState = UsersState.get(this.common);
        const freebetsData = usersState.freeBetsData.valueReady;
        const config = ConfigComponents.get(this.common);

        if (freebetsData !== null) {
            const totalAmount = freebetsData.response?.totalAmount;

            if (totalAmount !== undefined) {
                return config.precision.newFromAnything(totalAmount);
            }

            return null;
        }
        return null;
    }

    @computed public get shouldFreebetBeAllowed(): boolean {
        return this.freeBetsAmount === null
            ? false
            : this.betSlipSummaryState.totalStake.sub(this.freeBetsAmount).isGreaterThanZero();
    }

    @computed private get legsRawIds2(): Array<BetslipSingleId> {
        const tempLegsIds: Set<BetslipSingleId> = new Set();
        for (const leg of this.possibleBetsRequestState.coreLegsPossibleBetsRequest) {
            const selectionModel = leg.selectionId.getModel();
            if (selectionModel !== null) {
                tempLegsIds.add(this.ids.getSingleId(selectionModel.id2, 0));
            }
        }
        return Array.from(tempLegsIds);
    }

    @computed public get legsIds2(): Array<BetslipSingleId> {
        if (this.referralState.referralData !== null) {
            return this.referralState.legsIds2;
        }
        return this.legsRawIds2;
    }

    @computed public get firstSelection2(): BetslipSingleId | null {
        if (this.referralState.referralData !== null && this.referralState.legsIds2.length === 1) {
            return this.referralState.legsIds2[0] ?? null;
        }
        if (this.legsIds2.length === 1) {
            return this.legsIds2[0] ?? null;
        }
        return null;
    }

    /**
     * @deprecated - switch to combinationState.combinationsCast2
     */
    @computed public get combinationsCast2(): Array<BetslipCombinationId> {
        return this.combinationState.combinationsCast2;
    }

    /**
     * @deprecated - switch to combinationState.combinationWithoutCast2
     */
    @computed public get combinationWithoutCast2(): Array<BetslipCombinationId> {
        return this.combinationState.combinationWithoutCast2;
    }

    public isSelected = (id: SelectionId): boolean => {
        const legId = this.ids.getSingleId(id, 0);
        return this.legsIds2.includes(legId);
    };

    @action public onAcceptChanges = (): void => {
        for (const legId of this.legsIds2) {
            legId.getModel().acceptChanges();
        }

        this.rabState.acceptChanges();
    };

    @action public onAcceptUnavailableSelections = (): void => {
        this.unavailableSelectionsState.accept();
    };

    @computed public get elementRefHeight(): number {
        const elementRef = this.reactiveWrapper.ref;
        return elementRef?.clientHeight ?? 0;
    }

    @action public openBonusPopup = async (
        id: string,
        freeBets: Array<string>,
        isCombination = false
    ): Promise<void> => {
        if (this.accountBonusesResource.getReady() !== null) {
            await this.accountBonusesResource.refresh();
        }
        this.isBonusPopupOpen = true;
        this.selectionFreeBets = { id, freeBets, isCombination };
    };

    @action public closeBonusPopup = (): void => {
        this.isBonusPopupOpen = false;
        this.selectionFreeBets = null;
    };

    @action public applyBonuses = (bonuses: Array<SelectedBonusType>): void => {
        if (this.selectionFreeBets === null) {
            console.error('could not apply bonus to nullish selection');
            return;
        }

        this.selectedBonuses.set(this.selectionFreeBets.id, bonuses);

        if (this.selectionFreeBets.isCombination) {
            this.selectedBonuses.set(`all${this.selectionFreeBets.id}`, bonuses);
        }

        this.isBonusPopupOpen = false;
        this.selectionFreeBets = null;
    };

    public getSelectionAppliedBonuses = (id: string): Amount | null => {
        const selectedBonusesForSelectionId = this.selectedBonuses.get(id)?.filter((bonus) => bonus.selectionId === id);

        if (selectedBonusesForSelectionId === undefined || selectedBonusesForSelectionId.length === 0) {
            return null;
        }

        return selectedBonusesForSelectionId.reduce((sum, bonus) => sum.add(bonus.currentAmount), new Amount('0'));
    };

    @computed public get availableBonusFreeBets(): Array<{
        id: string;
        selectionId: string | null;
        label: string;
        expiry: string;
        currentAmount: Amount;
        rawAmount: number;
    }> | null {
        const config = ConfigComponents.get(this.common);
        const data = this.accountBonusesResource.getReady();

        if (data === null) {
            return null;
        }

        const accountFreeBets = data['free-bet'];

        if (accountFreeBets === undefined) {
            return [];
        }

        return accountFreeBets
            .filter(({ bonusId }) => this.selectionFreeBets?.freeBets.includes(bonusId))
            .map(({ expiryAt, bonusId, rewardName, currentAmount }) => {
                const expiryDate = DateTime.from(expiryAt);

                const expiresDateLabel = expiryDate === null ? 'N/A' : expiryDate.format('DD/MM/YYYY');

                const expiresTimeLabel = expiryDate === null ? 'N/A' : expiryDate.format('HH:mm');

                return {
                    selectionId: this.selectionFreeBets?.id ?? null,
                    id: bonusId,
                    label: rewardName ?? 'Free Bet',
                    rawAmount: currentAmount ?? 0,
                    currentAmount: config.precision.newFromAnything(currentAmount ?? 0),
                    expiry: `Expires on ${expiresDateLabel} at ${expiresTimeLabel}`,
                };
            });
    }
}
